import { inject, Injectable } from '@angular/core';
import { RedirectFunction, Router } from '@angular/router';

export const CLIENT_SESSION_STORAGE_PREFIX = 'reverb.client-session';
export function clientSessionStorageKey(clientId: string): string {
  return `${CLIENT_SESSION_STORAGE_PREFIX}.${clientId.trim().toLowerCase()}`;
}

export interface ClientPanelSizes { leftSize: number; middleSize: number; rightSize: number }

interface ClientSessionState {
  hideEmptyTests: boolean;
  pages: Record<string, string>;
  panels?: Record<string, ClientPanelSizes>;
}

/** Per-tab preferences survive reloads, and end with the browser session. */
@Injectable({ providedIn: 'root' })
export class ClientSessionStateService {
  private readonly router = inject(Router);
  private readonly fallback = new Map<string, ClientSessionState>();
  private readonly failedWrites = new Set<string>();

  hideEmptyTests(clientId: string): boolean {
    return this.read(clientId).hideEmptyTests;
  }

  setHideEmptyTests(clientId: string, hidden: boolean): void {
    this.write(clientId, { ...this.read(clientId), hideEmptyTests: hidden });
  }

  panelSizes(clientId: string, evaluationTypeId: string): ClientPanelSizes | null {
    const sizes = this.read(clientId).panels?.[evaluationTypeId.toLowerCase()];
    if (!sizes || ![sizes.leftSize, sizes.middleSize, sizes.rightSize].every(value =>
      typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100) ||
      Math.abs(sizes.leftSize + sizes.middleSize + sizes.rightSize - 100) > 0.1) return null;
    return { ...sizes };
  }

  rememberPanelSizes(clientId: string, evaluationTypeId: string, sizes: ClientPanelSizes): void {
    if (!clientId || !evaluationTypeId) return;
    const state = this.read(clientId);
    this.write(clientId, { ...state, panels: { ...state.panels, [evaluationTypeId.toLowerCase()]: {
      leftSize: sizes.leftSize, middleSize: sizes.middleSize, rightSize: sizes.rightSize,
    } } });
  }

  rememberPage(url: string): void {
    const scope = this.pageScope(url);
    if (!scope) return;
    const state = this.read(scope.clientId);
    this.write(scope.clientId, {
      ...state,
      pages: { ...state.pages, [scope.evaluationTypeId.toLowerCase()]: url },
    });
  }

  lastPage(clientId: string, evaluationTypeId: string): string | null {
    const url = this.read(clientId).pages[evaluationTypeId.toLowerCase()];
    const scope = typeof url === 'string' ? this.pageScope(url) : null;
    return scope && scope.clientId.toLowerCase() === clientId.toLowerCase() &&
      scope.evaluationTypeId.toLowerCase() === evaluationTypeId.toLowerCase() ? url : null;
  }

  private pageScope(url: string): { clientId: string; evaluationTypeId: string } | null {
    // Restore only a page inside this client shell, never a bare entry URL,
    // another application page, or an external destination.
    if (!url.startsWith('/client/')) return null;
    try {
      const tree = this.router.parseUrl(url);
      const primary = tree.root.children['primary'];
      const segments = primary?.segments.map(segment => segment.path) ?? [];
      if (Object.keys(tree.root.children).some(outlet => outlet !== 'primary') ||
          segments.length !== 4 || segments[0] !== 'client' || !segments[1] ||
          segments[2] !== 'evaluation-type' || !segments[3] ||
          !primary.children['left']?.segments.length ||
          Object.keys(primary.children).some(outlet => outlet !== 'left' && outlet !== 'right')) return null;
      return { clientId: segments[1], evaluationTypeId: segments[3] };
    } catch {
      return null;
    }
  }

  private read(clientId: string): ClientSessionState {
    const defaults: ClientSessionState = { hideEmptyTests: false, pages: {} };
    if (!clientId) return defaults;
    if (this.failedWrites.has(clientSessionStorageKey(clientId))) {
      return this.fallback.get(clientSessionStorageKey(clientId)) ?? defaults;
    }
    try {
      const raw = sessionStorage.getItem(clientSessionStorageKey(clientId));
      if (!raw) return this.fallback.get(clientSessionStorageKey(clientId)) ?? defaults;
      const value = JSON.parse(raw);
      if (!value || typeof value !== 'object') return defaults;
      return {
        hideEmptyTests: value.hideEmptyTests === true,
        pages: value.pages && typeof value.pages === 'object' && !Array.isArray(value.pages) ? value.pages : {},
        panels: value.panels && typeof value.panels === 'object' && !Array.isArray(value.panels) ? value.panels : {},
      };
    } catch {
      return this.fallback.get(clientSessionStorageKey(clientId)) ?? defaults;
    }
  }

  private write(clientId: string, state: ClientSessionState): void {
    if (!clientId) return;
    const key = clientSessionStorageKey(clientId);
    this.fallback.set(key, state);
    try {
      sessionStorage.setItem(key, JSON.stringify(state));
      this.failedWrites.delete(key);
    } catch {
      this.failedWrites.add(key);
      // Keep navigation and preferences usable if browser storage is unavailable.
    }
  }
}

export const clientSessionRedirect: RedirectFunction = route => {
  const clientId = route.params['clientId'];
  const evaluationTypeId = route.params['evaluationTypeId'];
  if (!clientId || !evaluationTypeId) return '/clients';
  return inject(ClientSessionStateService).lastPage(clientId, evaluationTypeId) ??
    `/client/${encodeURIComponent(clientId)}/evaluation-type/${encodeURIComponent(evaluationTypeId)}/(left:client-details)`;
};
